import { Button, IconButton, Tooltip, Box, Chip } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EditIcon from "@mui/icons-material/Edit";
import { getFullName, setFontColorBasedOnBackground } from "../../Utils/Utils";

export const getOnlineClientsColumns = (
  processOrder: (orderId: string) => void,
  setCommentModal: (modal: {
    open: boolean;
    orderId: string | null;
    currentComment: string;
  }) => void
) => [
  {
    label: Vocabulary.id,
    name: "_id",
    options: {
      sort: false,
      display: false,
    },
  },
  {
    label: Vocabulary.seenAt,
    name: "seenAt",
    options: {
      sort: false,
      display: false,
    },
  },
  {
    label: Vocabulary.isProcessed,
    name: "isProcessed",
    options: {
      sort: false,
      display: false,
    },
  },
  {
    label: Vocabulary.workPoint,
    name: "client_id",
    options: {
      sort: false,
      customBodyRender: (client: any) => {
        return client?.workPointId?.name;
      },
      exportValue: (client: any) => client?.workPointId?.name || "",
    },
  },
  {
    label: Vocabulary.status,
    name: "status",
    options: {
      setCellHeaderProps: () => ({
        align: "center",
      }),
      setCellProps: () => ({
        align: "center",
      }),
      filter: false,
      sort: false,
      customBodyRender: (status: any) => {
        return status ? (
          <Chip
            color="primary"
            key={status._id}
            label={status.name}
            style={{
              margin: 5,
              backgroundColor: status.color,
              color: setFontColorBasedOnBackground(status.color),
            }}
          />
        ) : (
          <Chip
            variant="outlined"
            label="Fără status"
            style={{
              margin: 5,
              color: "#666",
            }}
          />
        );
      },
      exportValue: (status: any) => status?.name || "Fără status",
    },
  },
  {
    label: Vocabulary.product,
    name: "product",
    options: {
      sort: false,
      customBodyRender: (object: any) => {
        return object?.name;
      },
      exportValue: (object: any) => object?.name || "",
    },
  },
  {
    label: Vocabulary.price,
    name: "product",
    options: {
      sort: true,
      customBodyRender: (value: any) => {
        return (
          <Box
            sx={{
              fontWeight: 600,
              color: "primary.main",
              fontSize: "0.95rem",
            }}
          >
            {value?.price}
          </Box>
        );
      },
      exportValue: (value: any) => value?.price ?? "",
    },
  },
  {
    label: Vocabulary.cursant,
    name: "client_id",
    options: {
      sort: false,
      customBodyRender: (client: any) => {
        return getFullName(client);
      },
      exportValue: (client: any) => getFullName(client),
    },
  },
  {
    label: Vocabulary.phone,
    name: "client_id",
    options: {
      sort: false,
      customBodyRender: (client: any) => {
        return (
          <>
            {client?.phone ? (
              <a
                key={client.phone}
                href={`tel:${client.phone}`}
                style={{ textDecoration: "none" }}
              >
                <Chip
                  icon={<LocalPhoneIcon fontSize="small" />}
                  color="info"
                  label={client.phone}
                  style={{ padding: 5 }}
                />
              </a>
            ) : null}
          </>
        );
      },
      exportValue: (client: any) => client?.phone || "",
    },
  },
  {
    label: Vocabulary.email,
    name: "client_id",
    options: {
      sort: false,
      customBodyRender: (client: any) => {
        return client?.email;
      },
      exportValue: (client: any) => client?.email || "",
    },
  },
  {
    label: Vocabulary.createdAt,
    name: "createdAt",
    options: {
      sort: true,
      customBodyRender: (value: string) => {
        return new Date(value).toLocaleDateString("ro-RO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      exportValue: (value: string) => value ? new Date(value).toLocaleDateString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) : "",
    },
  },
  {
    label: Vocabulary.comments,
    name: "comments",
    options: {
      sort: false,
      customBodyRender: (comments: string, tableMeta: any) => {
        const rowData = tableMeta.rowData;
        const orderId = rowData[0]; // _id

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "20vw",
            }}
          >
            <Box
              sx={{
                width: "100%",
                fontSize: "0.875rem",
                color: comments ? "text.primary" : "text.secondary",
                fontStyle: comments ? "normal" : "italic",
                wordWrap: "break-word",
              }}
            >
              {comments || "Fără comentarii"}
            </Box>
            <Tooltip title="Editează comentariu">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setCommentModal({
                    open: true,
                    orderId: orderId,
                    currentComment: comments || "",
                  });
                }}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.light",
                    color: "white",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
      exportValue: (comments: string) => comments || "",
    },
  },
  {
    label: "",
    name: "",
    options: {
      sort: false,
      filter: false,
      customBodyRender: (_: any, tableMeta: any) => {
        const rowData = tableMeta.rowData;
        const orderId = rowData[0]; // _id
        const isAlreadyProcessed = rowData[2]; // isProcessed

        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={isAlreadyProcessed ? Vocabulary.alreadyProcessed : Vocabulary.processOrder}>
              <Button
                variant="contained"
                size="small"
                disabled={isAlreadyProcessed}
                startIcon={<PlayArrowIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAlreadyProcessed) {
                    processOrder(orderId);
                  }
                }}
                sx={{
                  backgroundColor: isAlreadyProcessed ? "#9ca3af" : "#10b981",
                  color: "white",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  "&:hover": {
                    backgroundColor: isAlreadyProcessed ? "#9ca3af" : "#059669",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#9ca3af",
                    color: "white",
                  },
                }}
              >
                {isAlreadyProcessed ? Vocabulary.alreadyProcessed : Vocabulary.process}
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
  },
];
